/**
 * FriendCell — Shared list cell for friend/search results
 *
 * Layout: [Avatar + online dot] [Player Name + Level] [Heart] [Gift]
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { formatStacks } from './formatStacks';

export interface FriendCellData {
  id: string;
  username: string;
  picture: string;
  /** Soft-currency balance (Stacks) — shown in place of level. */
  stacks: number;
  level?: number;
  crewTag?: string;
  isOnline: boolean;
  isFavourite: boolean;
}

interface FriendCellEvents {
  'select': { playerId: string };
  'favourite': { playerId: string; isFavourite: boolean };
  'gift': { playerId: string };
}

export class FriendCell extends EventEmitter<FriendCellEvents> {
  public element: HTMLElement;
  private data: FriendCellData;

  constructor(data: FriendCellData) {
    super();
    this.data = data;
    this.element = this.createElement();
  }

  update(data: Partial<FriendCellData>): void {
    Object.assign(this.data, data);
    this.render();
  }

  getData(): FriendCellData {
    return this.data;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'crew-friend-cell';
    el.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.crew-cell-heart')) {
        this.data.isFavourite = !this.data.isFavourite;
        this.render();
        this.emit('favourite', { playerId: this.data.id, isFavourite: this.data.isFavourite });
        return;
      }
      if (target.closest('.crew-cell-gift')) {
        this.emit('gift', { playerId: this.data.id });
        return;
      }
      this.emit('select', { playerId: this.data.id });
    });
    this.element = el; // assign before render so this.element.innerHTML works
    this.render();
    return el;
  }

  private render(): void {
    const { username, picture, stacks, isOnline, isFavourite, crewTag } = this.data;
    const initial = username.charAt(0).toUpperCase();
    const avatarContent = picture
      ? `<img src="${picture}" alt="${initial}" class="crew-cell-avatar-img">`
      : `<span class="crew-cell-avatar-initial">${initial}</span>`;

    this.element.innerHTML = `
      <div class="crew-cell-avatar ${isOnline ? 'online' : ''}">
        ${avatarContent}
        ${isOnline ? '<span class="crew-cell-online-dot"></span>' : ''}
      </div>
      <div class="crew-cell-info">
        <div class="crew-cell-name">${username}</div>
        <div class="crew-cell-meta"><span class="crew-cell-stacks">$${formatStacks(stacks ?? 0)}</span>${crewTag ? ` · [${crewTag}]` : ''}</div>
      </div>
      <button class="crew-cell-heart ${isFavourite ? 'active' : ''}" aria-label="Favourite">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="${isFavourite ? '#FF0080' : 'none'}" stroke="${isFavourite ? '#FF0080' : 'rgba(255,255,255,0.3)'}" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
      <button class="crew-cell-gift" aria-label="Send Gift">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFD700" stroke-width="2">
          <polyline points="20 12 20 22 4 22 4 12"/>
          <rect x="2" y="7" width="20" height="5"/>
          <line x1="12" y1="22" x2="12" y2="7"/>
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
        </svg>
      </button>
    `;
  }
}
